
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../services/api";
import axiosPrivate from "../../services/axios/axiosPrivate";

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || error?.message || fallback;

/* GET ALL DESIGNATIONS */
export const fetchDesignations = createAsyncThunk(
  "designations/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.get(api.designations.getAll);
      const data = response?.data?.data;

      return Array.isArray(data) ? data : data?.content || [];
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to fetch designations")
      );
    }
  }
);

/* CREATE DESIGNATION */
export const createDesignation = createAsyncThunk(
  "designations/create",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.post(
        api.designations.getAll,
        payload
      );

      return response?.data?.data ?? payload;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to create designation")
      );
    }
  }
);

/* UPDATE DESIGNATION */
export const updateDesignation = createAsyncThunk(
  "designations/update",
  async ({ id, payload }, { rejectWithValue }) => {
    try {
      const response = await axiosPrivate.put(
        `${api.designations.getById}${id}`,
        payload
      );

      return {
        id,
        ...(response?.data?.data || payload),
      };
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to update designation")
      );
    }
  }
);

/* DEACTIVATE DESIGNATION */
export const deactivateDesignation = createAsyncThunk(
  "designations/deactivate",
  async (id, { rejectWithValue }) => {
    try {
      await axiosPrivate.delete(
        `${api.designations.deactivate}${id}`
      );

      return id;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to deactivate designation")
      );
    }
  }
);

/* ACTIVATE DESIGNATION */
export const activateDesignation = createAsyncThunk(
  "designations/activate",
  async (id, { rejectWithValue }) => {
    try {
      await axiosPrivate.put(
        `${api.designations.activate}${id}`
      );

      return id;
    } catch (error) {
      return rejectWithValue(
        getErrorMessage(error, "Failed to activate designation")
      );
    }
  }
);

const initialState = {
  list: [],
  loading: false,
  error: null,
  createLoading: false,
  updateLoading: false,
  statusUpdatingId: null,
  actionError: null,
};

const designationsSlice = createSlice({
  name: "designations",
  initialState,

  reducers: {
    clearDesignationErrors(state) {
      state.error = null;
      state.actionError = null;
    },
  },

  extraReducers: (builder) => {
    builder

      /* FETCH DESIGNATIONS */
      .addCase(fetchDesignations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDesignations.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload || [];
      })
      .addCase(fetchDesignations.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload || "Failed to fetch designations";
      })

      /* CREATE DESIGNATION */
      .addCase(createDesignation.pending, (state) => {
        state.createLoading = true;
        state.actionError = null;
      })
      .addCase(createDesignation.fulfilled, (state, action) => {
        state.createLoading = false;

        const created = action.payload;

        if (created && created.id != null) {
          const exists = state.list.some(
            (item) => String(item.id) === String(created.id)
          );

          if (!exists) {
            state.list.unshift(created);
          }
        }
      })
      .addCase(createDesignation.rejected, (state, action) => {
        state.createLoading = false;
        state.actionError =
          action.payload || "Failed to create designation";
      })

      /* UPDATE DESIGNATION */
      .addCase(updateDesignation.pending, (state) => {
        state.updateLoading = true;
        state.actionError = null;
      })
      .addCase(updateDesignation.fulfilled, (state, action) => {
        state.updateLoading = false;

        const updated = action.payload;

        const index = state.list.findIndex(
          (item) => String(item.id) === String(updated.id)
        );

        if (index !== -1) {
          state.list[index] = {
            ...state.list[index],
            ...updated,
          };
        }
      })
      .addCase(updateDesignation.rejected, (state, action) => {
        state.updateLoading = false;
        state.actionError =
          action.payload || "Failed to update designation";
      })

      /* DEACTIVATE DESIGNATION */
      .addCase(deactivateDesignation.pending, (state, action) => {
        state.statusUpdatingId = action.meta.arg;
        state.actionError = null;
      })
      .addCase(deactivateDesignation.fulfilled, (state, action) => {
        state.statusUpdatingId = null;

        const designation = state.list.find(
          (item) => String(item.id) === String(action.payload)
        );

        if (designation) {
          designation.delete = true;
        }
      })
      .addCase(deactivateDesignation.rejected, (state, action) => {
        state.statusUpdatingId = null;
        state.actionError =
          action.payload || "Failed to deactivate designation";
      })

      /* ACTIVATE DESIGNATION */
      .addCase(activateDesignation.pending, (state, action) => {
        state.statusUpdatingId = action.meta.arg;
        state.actionError = null;
      })
      .addCase(activateDesignation.fulfilled, (state, action) => {
        state.statusUpdatingId = null;

        const designation = state.list.find(
          (item) => String(item.id) === String(action.payload)
        );

        if (designation) {
          designation.delete = false;
        }
      })
      .addCase(activateDesignation.rejected, (state, action) => {
        state.statusUpdatingId = null;
        state.actionError =
          action.payload || "Failed to activate designation";
      });
  },
});

export const { clearDesignationErrors } = designationsSlice.actions;

export default designationsSlice.reducer;
